import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Typography, Alert } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridActionsCellItem } from '@mui/x-data-grid-premium';
import { ViewToolbar } from '@/components/ViewToolbar';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { useRoles, useDeleteRole } from '@/hooks/identity/useIdentity';
import { IdentityRole } from '@/types/identity/Identity';

export function RoleListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useRoles();
  const deleteMutation = useDeleteRole();
  const [actionError, setActionError] = useState<string | null>(null);

  const handleDelete = useCallback(
    (id: string, name: string, isStatic: boolean) => {
      if (isStatic) {
        setActionError(t('identity.cannotDeleteStaticRole', { name }));
        return;
      }
      if (!confirm(t('identity.confirmDeleteRole', { name }))) return;
      setActionError(null);
      deleteMutation.mutate(id, {
        onSuccess: () => {
          refetch();
        },
        onError: () => setActionError(t('identity.deleteRoleFailed')),
      });
    },
    [deleteMutation, refetch, t],
  );

  const columns = useMemo<GridColDef<IdentityRole>[]>(
    () => [
      {
        field: '_rowActions',
        type: 'actions',
        headerName: '',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label={t('common.edit')}
            onClick={() => navigate(`/core/roles/${params.id}`)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label={t('common.delete')}
            onClick={() =>
              handleDelete(params.id as string, params.row.name, params.row.isStatic)
            }
            disabled={params.row.isStatic}
          />,
        ],
      },
      { field: 'name', headerName: t('identity.roleName'), width: 240 },
      {
        field: 'isDefault',
        headerName: t('identity.isDefaultShort'),
        width: 100,
        renderCell: (params) =>
          params.value ? <Chip label={t('identity.isDefaultShort')} size="small" color="info" /> : null,
      },
      {
        field: 'isStatic',
        headerName: t('identity.isStatic'),
        width: 100,
        renderCell: (params) =>
          params.value ? <Chip label={t('identity.isStatic')} size="small" color="warning" variant="outlined" /> : null,
      },
      {
        field: 'isPublic',
        headerName: t('identity.isPublic'),
        width: 100,
        renderCell: (params) =>
          params.value ? <Chip label={t('identity.isPublic')} size="small" color="success" variant="outlined" /> : null,
      },
    ],
    [navigate, handleDelete, t],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ViewToolbar onRefresh={() => refetch()}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate('/core/roles/new')}
          sx={{ textTransform: 'none', fontWeight: 500, px: 2 }}
        >
          {t('identity.newRole')}
        </Button>
      </ViewToolbar>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
          {t('identity.roles')}
        </Typography>
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 1 }}>
            {actionError}
          </Alert>
        )}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGridPremium
            rows={data?.items ?? []}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            density="compact"
            rowHeight={GRID_ROW_HEIGHT}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(params) => navigate(`/core/roles/${params.id}`)}
          />
        </Box>
      </Box>
    </Box>
  );
}
